// bluaIQ — plan-upload success copy (bug 1, Codex #471). PURE, no React, so the
// multi-PDF/multi-set TOTAL and the real pluralization are unit-testable.
//
// The defect this fixes: the success line used only the LAST set's sheet count
// (`last.sheets.length`), so two PDFs of 3 and 5 sheets reported "5 hoja(s)".
// Here the count is the TRUE total across the batch, pluralized for real (1 hoja
// / N hojas · 1 set / N sets), in ES and EN.

/** `${n} <singular|plural>`, language-aware. */
export function pluralUnit(n: number, es1: string, esN: string, en1: string, enN: string, isEs: boolean): string {
  const word = isEs ? (n === 1 ? es1 : esN) : (n === 1 ? en1 : enN)
  return `${n} ${word}`
}

export const sheetsLabel = (n: number, isEs: boolean) => pluralUnit(n, 'hoja', 'hojas', 'sheet', 'sheets', isEs)
export const setsLabel = (n: number, isEs: boolean) => pluralUnit(n, 'set', 'sets', 'set', 'sets', isEs)

export interface PlanUploadSuccess {
  decision: 'new' | 'add' | 'replace'
  /** how many NEW plan sets were created this batch (decision 'new'). */
  createdSets: number
  /** files uploaded successfully. */
  filesOk: number
  /** TRUE total sheets uploaded across the whole batch (sum of per-upload adds). */
  totalSheets: number
  /** sheets in the resulting ACTIVE set (for the 'replace'/current-set phrasing). */
  currentSetSheets: number
  /** files that failed. */
  failed: number
}

/** Build the success line. TOTAL is explicit ("N hojas en total"); the 'replace'
 *  path names the CURRENT set instead ("Set actual: N hojas"), never a loose,
 *  misleading number (Codex #471). */
export function formatPlanUploadSuccess(x: PlanUploadSuccess, isEs: boolean): string {
  let base: string
  if (x.decision === 'new') {
    base = x.createdSets <= 1
      ? (isEs ? `Plano subido: ${sheetsLabel(x.totalSheets, true)}` : `Plan uploaded: ${sheetsLabel(x.totalSheets, false)}`)
      : (isEs
          ? `${setsLabel(x.createdSets, true)} de planos creados · ${sheetsLabel(x.totalSheets, true)} en total`
          : `${setsLabel(x.createdSets, false)} created · ${sheetsLabel(x.totalSheets, false)} total`)
  } else if (x.decision === 'add') {
    const files = isEs
      ? `${x.filesOk} ${x.filesOk === 1 ? 'archivo agregado' : 'archivos agregados'}`
      : `${x.filesOk} ${x.filesOk === 1 ? 'file added' : 'files added'}`
    base = isEs
      ? `${files} al set actual · ${sheetsLabel(x.totalSheets, true)} en total`
      : `${files} to the current set · ${sheetsLabel(x.totalSheets, false)} total`
  } else {
    // replace — "total" is ambiguous after a replace, so name the current set.
    base = isEs
      ? `Set reemplazado · Set actual: ${sheetsLabel(x.currentSetSheets, true)}`
      : `Set replaced · Current set: ${sheetsLabel(x.currentSetSheets, false)}`
  }
  if (x.failed > 0) {
    base += isEs
      ? ` · ${x.failed} ${x.failed === 1 ? 'falló' : 'fallaron'}`
      : ` · ${x.failed} failed`
  }
  return base
}
